import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  type GestureResponderEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type PressableStateCallbackType,
  type ViewStyle,
} from "react-native";
import { NestableDraggableFlatList, NestableScrollContainer, ScaleDecorator } from "react-native-draggable-flatlist";
import {
  CaretDownIcon,
  CaretRightIcon,
  ChatCircleIcon,
  CheckCircleIcon,
  DotsThreeIcon,
  PlusIcon,
  StackSimpleIcon,
  TrashIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { ModernizedActivityEntityType } from "@axio/graphql/generated/graphql";

import { PopoverMenu } from "../../../../../components/common/general/PopoverMenu/PopoverMenu";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import FilterPill from "./FilterPill";
import type { CourseModernizerReviewPermissions } from "../reviewPermissions";
import type {
  CourseEvaluatorReviewActivityMove,
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
  CourseEvaluatorReviewSidebarMeta,
  CourseEvaluatorReviewSidebarTag,
} from "../types/reviewTypes";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "../constants/itemTypeLabels";

interface ReviewSidebarProps {
  review: CourseEvaluatorReviewData;
  selectedItemId: string | null;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  commentedItemIds?: Set<string>;
  commentedItemCounts?: Map<string, number>;
  unresolvedItemCounts?: Map<string, number>;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
  onAddModule?: () => void;
  onDeleteActivity?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onUndoDeleteActivity?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onDeleteModule?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onUndoDeleteModule?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onAddActivity?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onAcceptGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onRejectGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onModuleDragEnd?: (groups: CourseEvaluatorReviewSidebarGroup[]) => void | Promise<void>;
  onActivityDragEnd?: (
    group: CourseEvaluatorReviewSidebarGroup,
    items: CourseEvaluatorReviewSidebarItem[]
  ) => void | Promise<void>;
  onActivityMoveEnd?: (move: CourseEvaluatorReviewActivityMove) => void | Promise<void>;
  permissions: CourseModernizerReviewPermissions;
}

interface CrossModuleActivityDragState {
  sourceGroupId: string;
  itemId: string;
}

interface CrossModuleActivityDropTarget {
  targetGroupId: string;
  beforeItemId?: string;
}

function getTagPresentation(tag: CourseEvaluatorReviewSidebarTag) {
  switch (tag) {
    case "instructional":
      return {
        label: "Instructional",
        backgroundColor: "#E6F3FF",
        textColor: "#0069CC",
      };
    case "lecture":
      return {
        label: "Lecture",
        backgroundColor: "#F5F0FE",
        textColor: "#7244BB",
      };
    case "supplementary":
      return {
        label: "Supplementary",
        backgroundColor: "#F2F4F7",
        textColor: "#344054",
      };
    case "new":
    default:
      return {
        label: "New",
        backgroundColor: "#E7F7F8",
        textColor: "#15727D",
      };
  }
}

function getItemTypePresentation(itemType: CourseEvaluatorReviewItemType) {
  switch (itemType) {
    case "assignment":
    case "project":
    case "moduleDescription":
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType],
        color: itemType === "moduleDescription" ? "#7C3AED" : "#667085",
        icon: itemType === "moduleDescription" ? ("moduleDescription" as const) : undefined,
      };
    case "externalLink":
    case "discussion":
    case "quiz":
    case "file":
    case "resource":
    case "link":
    case "htmlContent":
    case "ile":
    case "checklist":
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType],
        color: "#667085",
      };
    case "page":
    case "webpage":
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType],
        color: "#D97706",
      };
    default:
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS.checklist,
        color: "#667085",
      };
  }
}

function getEffectivenessColor(effectiveness: CourseEvaluatorReviewEffectiveness) {
  switch (effectiveness) {
    case "excellent":
      return "#3B7C0F";
    case "fair":
      return "#B54708";
    case "poor":
      return "#D92D20";
    case "good":
    default:
      return "#3459D4";
  }
}

function DecisionIcon({ decision }: { decision: CourseEvaluatorReviewDecision }) {
  if (decision === "accepted") {
    return (
      <View style={styles.decisionIconBackground}>
        <CheckCircleIcon size={14} color={colors.success[400]} weight="bold" />
      </View>
    );
  }
  if (decision === "rejected") {
    return (
      <View style={styles.decisionIconBackground}>
        <XCircleIcon size={14} color={colors.error[500]} weight="bold" />
      </View>
    );
  }
  if (decision === "synced") {
    return (
      <View style={styles.decisionIconBackground}>
        <CheckCircleIcon size={14} color={colors.brand[400]} weight="bold" />
      </View>
    );
  }
  return <View style={styles.sidebarRadio} />;
}

function getItemBorderColor(decision: CourseEvaluatorReviewDecision) {
  if (decision === "accepted") return colors.success[500];
  if (decision === "rejected") return colors.error[500];
  if (decision === "synced") return colors.brand[600];
  return "transparent";
}

function renderMeta(itemId: string, meta: CourseEvaluatorReviewSidebarMeta, index: number) {
  if (meta.kind === "tag") {
    const tag = getTagPresentation(meta.tag);

    return (
      <View
        key={`${itemId}-tag-${index}`}
        style={[
          styles.sidebarPill,
          {
            backgroundColor: tag.backgroundColor,
          },
        ]}>
        <CustomText
          text={tag.label}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={{ color: tag.textColor }}
        />
      </View>
    );
  }

  if (meta.kind === "itemType") {
    const itemType = getItemTypePresentation(meta.itemType);

    return (
      <View key={`${itemId}-item-type-${index}`} style={styles.sidebarFooterTextRow}>
        {itemType.icon === "moduleDescription" && <StackSimpleIcon size={10} color={itemType.color} weight="bold" />}
        <CustomText
          text={itemType.label}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={[styles.sidebarFooterText, { color: itemType.color }]}
        />
      </View>
    );
  }

  if (meta.kind === "manual") {
    return (
      <View key={`${itemId}-manual-${index}`} style={styles.manualPill}>
        <CustomText text="MANUAL" useTranslationText={false} size="xs" weight="medium" style={styles.manualMetaText} />
      </View>
    );
  }

  return (
    <View key={`${itemId}-effectiveness-${index}`} style={styles.sidebarFooterTextRow}>
      <CustomText
        text={meta.effectiveness.charAt(0).toUpperCase() + meta.effectiveness.slice(1)}
        useTranslationText={false}
        size="xs"
        weight="normal"
        style={[styles.sidebarFooterText, { color: getEffectivenessColor(meta.effectiveness) }]}
      />
    </View>
  );
}

function isManualItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  return item.meta?.some((meta) => meta.kind === "manual") === true;
}

function getManualItemType(item: CourseEvaluatorReviewSidebarItem): CourseEvaluatorReviewItemType | undefined {
  const itemTypeMeta = item.meta?.find(
    (meta): meta is Extract<CourseEvaluatorReviewSidebarMeta, { kind: "itemType" }> => meta.kind === "itemType"
  );

  return itemTypeMeta?.itemType;
}

function isNewSidebarItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  return item.meta?.some((meta) => meta.kind === "tag" && meta.tag === "new") === true;
}

function getModuleDescriptionItem(group: CourseEvaluatorReviewSidebarGroup): CourseEvaluatorReviewSidebarItem | null {
  return group.items.find((item) => item.entityType === ModernizedActivityEntityType.Module) ?? null;
}

function isDeletableActivityItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  if (item.entityType === ModernizedActivityEntityType.IleRecommendation) {
    return isNewSidebarItem(item);
  }

  return (
    item.entityType === ModernizedActivityEntityType.Assignment ||
    item.entityType === ModernizedActivityEntityType.Project ||
    item.entityType === ModernizedActivityEntityType.OtherActivity
  );
}

function renderManualMeta(item: CourseEvaluatorReviewSidebarItem) {
  const itemType = getManualItemType(item);
  const newTag = item.meta?.find(
    (meta): meta is Extract<CourseEvaluatorReviewSidebarMeta, { kind: "tag" }> =>
      meta.kind === "tag" && meta.tag === "new"
  );

  return (
    <View style={styles.sidebarItemMeta}>
      {newTag ? renderMeta(item.id, newTag, 0) : null}
      <View style={styles.manualPill}>
        <CustomText text="MANUAL" useTranslationText={false} size="xs" weight="medium" style={styles.manualMetaText} />
      </View>
      {itemType ? (
        <CustomText
          text={COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType]}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.manualItemTypeText}
        />
      ) : null}
    </View>
  );
}

function isCourseLevelGroup(group: CourseEvaluatorReviewSidebarGroup): boolean {
  return group.id === "course-level-content";
}

function formatModuleMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function getModuleGroups(groups: CourseEvaluatorReviewSidebarGroup[]): CourseEvaluatorReviewSidebarGroup[] {
  return groups.filter((group) => !isCourseLevelGroup(group));
}

function isModuleDescriptionItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  return item.kind === "moduleDescription";
}

function getActivityItems(items: CourseEvaluatorReviewSidebarItem[]): CourseEvaluatorReviewSidebarItem[] {
  return items.filter((item) => !isModuleDescriptionItem(item));
}

function orderItemsById<TItem extends { id: string }>(items: TItem[], orderedIds: string[] | undefined): TItem[] {
  if (!orderedIds?.length) {
    return items;
  }

  const itemById = new Map(items.map((item) => [item.id, item]));
  const orderedItems = orderedIds.map((id) => itemById.get(id)).filter((item): item is TItem => Boolean(item));
  const orderedIdSet = new Set(orderedIds);
  const missingItems = items.filter((item) => !orderedIdSet.has(item.id));

  return [...orderedItems, ...missingItems];
}

function ModuleDragHandle() {
  return (
    <View style={styles.dragDotGrid}>
      <View style={styles.dragDotRow}>
        <View style={styles.dragDot} />
        <View style={styles.dragDot} />
      </View>
      <View style={styles.dragDotRow}>
        <View style={styles.dragDot} />
        <View style={styles.dragDot} />
      </View>
    </View>
  );
}

function ReviewSidebar({
  review,
  selectedItemId,
  filterMode,
  collapsedSectionIds,
  commentedItemIds,
  unresolvedItemCounts,
  commentedItemCounts,
  getDecision,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
  onAddModule,
  onDeleteActivity,
  onUndoDeleteActivity,
  onDeleteModule,
  onUndoDeleteModule,
  onAddActivity,
  onAcceptGroup,
  onRejectGroup,
  onModuleDragEnd,
  onActivityDragEnd,
  onActivityMoveEnd,
  permissions,
}: ReviewSidebarProps) {
  const moduleGroups = useMemo(() => getModuleGroups(review.sidebarGroups), [review.sidebarGroups]);
  const moduleGroupById = useMemo(() => new Map(moduleGroups.map((group) => [group.id, group])), [moduleGroups]);
  const moduleOrderSignature = moduleGroups.map((group) => group.id).join("|");
  const activityOrderSignature = moduleGroups
    .map(
      (group) =>
        `${group.id}:${getActivityItems(group.items)
          .map((item) => item.id)
          .join("|")}`
    )
    .join("::");
  const [orderedModuleGroupIds, setOrderedModuleGroupIds] = useState<string[]>(() =>
    moduleGroups.map((group) => group.id)
  );
  const [orderedActivityItemIdsByGroupId, setOrderedActivityItemIdsByGroupId] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(moduleGroups.map((group) => [group.id, getActivityItems(group.items).map((item) => item.id)]))
  );
  const [crossModuleActivityDrag, setCrossModuleActivityDrag] = useState<CrossModuleActivityDragState | null>(null);
  const [crossModuleActivityDropTarget, setCrossModuleActivityDropTarget] =
    useState<CrossModuleActivityDropTarget | null>(null);
  const suppressedActivityDragEndGroupIdRef = useRef<string | null>(null);

  useEffect(() => {
    setOrderedModuleGroupIds(moduleGroups.map((group) => group.id));
  }, [moduleGroups, moduleOrderSignature]);

  useEffect(() => {
    setOrderedActivityItemIdsByGroupId(
      Object.fromEntries(moduleGroups.map((group) => [group.id, getActivityItems(group.items).map((item) => item.id)]))
    );
  }, [activityOrderSignature, moduleGroups]);

  const courseLevelGroups = review.sidebarGroups.filter(isCourseLevelGroup);
  const orderedModuleGroups = orderedModuleGroupIds
    .map((groupId) => moduleGroupById.get(groupId))
    .filter((group): group is CourseEvaluatorReviewSidebarGroup => Boolean(group));
  const canReorderModules =
    filterMode === "all" && permissions.canEdit && Boolean(onModuleDragEnd) && orderedModuleGroups.length > 1;
  const canMoveActivitiesAcrossModules = filterMode === "all" && permissions.canEdit && Boolean(onActivityMoveEnd);
  const canReorderActivities =
    filterMode === "all" && permissions.canEdit && (Boolean(onActivityDragEnd) || canMoveActivitiesAcrossModules);
  const canUseNestableDrag = canReorderModules || canReorderActivities;

  const getOrderedActivityItemsForGroup = (group: CourseEvaluatorReviewSidebarGroup) =>
    orderItemsById(getActivityItems(group.items), orderedActivityItemIdsByGroupId[group.id]);

  const handleModuleDragEnd = ({ data }: { data: CourseEvaluatorReviewSidebarGroup[] }) => {
    const previousModuleGroupIds = orderedModuleGroupIds;
    setOrderedModuleGroupIds(data.map((group) => group.id));
    void Promise.resolve(onModuleDragEnd?.(data)).catch(() => {
      setOrderedModuleGroupIds(previousModuleGroupIds);
    });
  };

  const handleActivityDragEnd = (
    group: CourseEvaluatorReviewSidebarGroup,
    data: CourseEvaluatorReviewSidebarItem[]
  ) => {
    if (!onActivityDragEnd) return;
    if (suppressedActivityDragEndGroupIdRef.current === group.id) {
      suppressedActivityDragEndGroupIdRef.current = null;
      return;
    }
    if (
      crossModuleActivityDrag?.sourceGroupId === group.id &&
      crossModuleActivityDropTarget &&
      crossModuleActivityDropTarget.targetGroupId !== group.id
    ) {
      return;
    }

    const previousActivityItemIdsByGroupId = orderedActivityItemIdsByGroupId;
    setOrderedActivityItemIdsByGroupId((current) => ({
      ...current,
      [group.id]: data.map((item) => item.id),
    }));
    void Promise.resolve(onActivityDragEnd?.(group, data)).catch(() => {
      setOrderedActivityItemIdsByGroupId(previousActivityItemIdsByGroupId);
    });
  };

  const updateCrossModuleActivityDropTarget = (targetGroupId: string, beforeItemId?: string) => {
    if (!crossModuleActivityDrag || crossModuleActivityDrag.sourceGroupId === targetGroupId) {
      return;
    }

    setCrossModuleActivityDropTarget(beforeItemId ? { targetGroupId, beforeItemId } : { targetGroupId });
  };

  const clearCrossModuleActivityDrag = () => {
    setCrossModuleActivityDrag(null);
    setCrossModuleActivityDropTarget(null);
  };

  const handleCrossModuleActivityDrop = () => {
    const dragState = crossModuleActivityDrag;
    const dropTarget = crossModuleActivityDropTarget;

    if (!dragState || !dropTarget || dragState.sourceGroupId === dropTarget.targetGroupId) {
      clearCrossModuleActivityDrag();
      return;
    }

    const sourceGroup = moduleGroupById.get(dragState.sourceGroupId);
    const targetGroup = moduleGroupById.get(dropTarget.targetGroupId);
    if (!sourceGroup || !targetGroup) {
      clearCrossModuleActivityDrag();
      return;
    }

    const sourceItemsBeforeMove = getOrderedActivityItemsForGroup(sourceGroup);
    const targetItemsBeforeMove = getOrderedActivityItemsForGroup(targetGroup);
    const movedItem = sourceItemsBeforeMove.find((item) => item.id === dragState.itemId);
    if (!movedItem) {
      clearCrossModuleActivityDrag();
      return;
    }

    const sourceItems = sourceItemsBeforeMove.filter((item) => item.id !== movedItem.id);
    const targetItemsWithoutMovedItem = targetItemsBeforeMove.filter((item) => item.id !== movedItem.id);
    const beforeItemIndex = dropTarget.beforeItemId
      ? targetItemsWithoutMovedItem.findIndex((item) => item.id === dropTarget.beforeItemId)
      : -1;
    const targetItems =
      beforeItemIndex >= 0
        ? [
            ...targetItemsWithoutMovedItem.slice(0, beforeItemIndex),
            movedItem,
            ...targetItemsWithoutMovedItem.slice(beforeItemIndex),
          ]
        : [...targetItemsWithoutMovedItem, movedItem];

    const previousActivityItemIdsByGroupId = orderedActivityItemIdsByGroupId;
    setOrderedActivityItemIdsByGroupId((current) => ({
      ...current,
      [sourceGroup.id]: sourceItems.map((item) => item.id),
      [targetGroup.id]: targetItems.map((item) => item.id),
    }));
    suppressedActivityDragEndGroupIdRef.current = sourceGroup.id;
    clearCrossModuleActivityDrag();

    void Promise.resolve(
      onActivityMoveEnd?.({
        sourceGroup,
        targetGroup,
        movedItem,
        sourceItems,
        targetItems,
      })
    ).catch(() => {
      setOrderedActivityItemIdsByGroupId(previousActivityItemIdsByGroupId);
    });
  };

  const renderSidebarItem = (
    item: CourseEvaluatorReviewSidebarItem,
    drag?: (event?: unknown) => void,
    isActive = false,
    group?: CourseEvaluatorReviewSidebarGroup
  ) => {
    const decision = getDecision(item.id);
    const borderColor = getItemBorderColor(decision);
    const manualItem = isManualItem(item);
    const commentCount = commentedItemCounts?.get(item.id) ?? 0;
    const unresolvedCount = unresolvedItemCounts?.get(item.id) ?? 0;
    const hasComments = (commentedItemIds?.has(item.id) ?? false) && commentCount > 0;
    const allResolved = hasComments && unresolvedCount === 0;
    const isActivityPendingDelete =
      item.entityType !== ModernizedActivityEntityType.Module && item.pendingDelete === true;
    const canUseItemAsDropTarget =
      group !== undefined &&
      !isCourseLevelGroup(group) &&
      group.pendingDelete !== true &&
      getModuleDescriptionItem(group)?.pendingDelete !== true;
    const isDraggable = Boolean(drag) && !isActivityPendingDelete;
    const canShowActivityMenu =
      isDeletableActivityItem(item) && Boolean(isActivityPendingDelete ? onUndoDeleteActivity : onDeleteActivity);
    const handleActivityDragStart = (event: GestureResponderEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      if (group && canMoveActivitiesAcrossModules) {
        setCrossModuleActivityDrag({ sourceGroupId: group.id, itemId: item.id });
        setCrossModuleActivityDropTarget({ targetGroupId: group.id });
      }
      drag?.(event);
    };
    const isCrossModuleDropIndicator =
      Boolean(group) &&
      canUseItemAsDropTarget &&
      Boolean(crossModuleActivityDrag) &&
      crossModuleActivityDrag?.sourceGroupId !== group?.id &&
      crossModuleActivityDropTarget?.targetGroupId === group?.id &&
      crossModuleActivityDropTarget.beforeItemId === item.id;

    const row = (
      <Pressable
        key={item.id}
        onPress={() => onSelectItem(item.id)}
        onPointerEnter={() => {
          if (group && canUseItemAsDropTarget) {
            updateCrossModuleActivityDropTarget(group.id, item.id);
          }
        }}
        onPointerUp={handleCrossModuleActivityDrop}
        role="button"
        accessibilityRole="button"
        accessibilityLabel={`Select ${item.title}`}
        style={({ hovered }: PressableStateCallbackType) => [
          styles.sidebarItem,
          { borderLeftWidth: 2, borderLeftColor: borderColor },
          selectedItemId === item.id && styles.sidebarItemActive,
          hovered && selectedItemId !== item.id && styles.sidebarItemHover,
          isActive && styles.sidebarItemDragging,
          isCrossModuleDropIndicator && styles.sidebarItemDropTarget,
        ]}>
        <View style={styles.sidebarItemRow}>
          <DecisionIcon decision={decision} />
          <View style={styles.sidebarItemContent}>
            <CustomText
              text={item.title}
              useTranslationText={false}
              size="xs"
              weight="normal"
              style={styles.sidebarItemTitle}
            />
            {manualItem ? renderManualMeta(item) : null}
            {!manualItem && item.meta?.length ? (
              <View style={styles.sidebarItemMeta}>
                {item.meta.map((meta, index) => renderMeta(item.id, meta, index))}
              </View>
            ) : null}
          </View>
          {hasComments && (
            <View style={[styles.commentMarkerWrap, allResolved && styles.commentMarkerResolved]}>
              <ChatCircleIcon
                size={14}
                color={allResolved ? "#6B7280" : "#a855f7"}
                weight="fill"
                style={styles.commentMarkerIcon}
              />
              <CustomText
                text={`${allResolved ? commentCount : unresolvedCount}`}
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={[styles.commentMarkerCount, allResolved && styles.commentMarkerCountResolved]}
              />
            </View>
          )}
          {decision === "synced" && (
            <View style={styles.publishedPill}>
              <CustomText
                text="Published"
                useTranslationText={false}
                size="xs"
                weight="bold"
                style={styles.publishedPillText}
              />
            </View>
          )}
          {isActivityPendingDelete ? (
            <View style={styles.pendingDeletePill}>
              <CustomText
                text="Pending delete"
                useTranslationText={false}
                size="xs"
                weight="bold"
                style={styles.pendingDeletePillText}
              />
            </View>
          ) : null}
          {!isActivityPendingDelete && item.removalRecommended ? (
            <View style={styles.removalPill}>
              <TrashIcon size={9} color="#B42318" weight="bold" />
              <CustomText
                text="Removal suggested"
                useTranslationText={false}
                size="xs"
                weight="bold"
                style={styles.removalPillText}
              />
            </View>
          ) : null}
          <View style={styles.sidebarItemActionSlot}>
            {isDraggable ? (
              <Pressable
                onPointerDown={handleActivityDragStart}
                hitSlop={4}
                role="button"
                accessibilityRole="button"
                accessibilityLabel={`Drag ${item.title} activity`}
                aria-label={`Drag ${item.title} activity`}
                style={({ hovered, pressed }: PressableStateCallbackType) => [
                  styles.activityDragHandle,
                  isActive && styles.moduleDragHandleDragging,
                  (hovered || pressed) && styles.moduleDragHandleHover,
                ]}>
                <ModuleDragHandle />
              </Pressable>
            ) : null}
          </View>
          <View style={styles.sidebarItemMenuSlot}>
            {canShowActivityMenu ? (
              <PopoverMenu
                triggerAccessibilityLabel={`${item.title} options`}
                triggerStyle={styles.itemDots}
                menuWidth={144}
                items={[
                  isActivityPendingDelete
                    ? {
                        key: "undo-delete-activity",
                        label: "Undo delete",
                        onPress: () => {
                          if (!permissions.canRestoreVersion) return;
                          onUndoDeleteActivity?.(item);
                        },
                        disabled: !permissions.canRestoreVersion,
                      }
                    : {
                        key: "delete-activity",
                        label: "Delete activity",
                        danger: true,
                        onPress: () => {
                          if (!permissions.canEdit) return;
                          onDeleteActivity?.(item);
                        },
                        disabled: !permissions.canEdit,
                      },
                ]}
                renderTrigger={() => <DotsThreeIcon size={16} color={colors.grey[500]} weight="bold" />}
              />
            ) : null}
          </View>
        </View>
      </Pressable>
    );

    if (!isDraggable) {
      return row;
    }

    return <ScaleDecorator key={item.id}>{row}</ScaleDecorator>;
  };

  const renderSidebarSection = (
    group: CourseEvaluatorReviewSidebarGroup,
    drag?: (event?: unknown) => void,
    isActive = false
  ) => {
    const isCollapsed = collapsedSectionIds.has(group.id);
    const decidedInGroup = group.decided;
    const moduleItem = getModuleDescriptionItem(group);
    const isModulePendingDelete =
      !isCourseLevelGroup(group) && (group.pendingDelete === true || moduleItem?.pendingDelete === true);
    const isDraggable = Boolean(drag) && !isModulePendingDelete;
    const handleModuleDragStart = (event: GestureResponderEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      drag?.(event);
    };

    const section = (
      <View key={group.id} style={[styles.sidebarSection, isActive && styles.sidebarSectionDragging]}>
        <View style={styles.sidebarSectionHeader}>
          <Pressable
            onPress={() => onToggleSection(group.id)}
            role="button"
            accessibilityRole="button"
            accessibilityLabel={`${isCollapsed ? "Expand" : "Collapse"} ${group.title}`}
            style={styles.sidebarSectionToggle}>
            {isCollapsed ? (
              <CaretRightIcon size={14} color={colors.grey[700]} />
            ) : (
              <CaretDownIcon size={14} color={colors.grey[700]} />
            )}
            <CustomText
              text={group.title}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={styles.sectionTitle}
            />
            {isModulePendingDelete ? (
              <View style={styles.pendingDeletePill}>
                <CustomText
                  text="Pending delete"
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.pendingDeletePillText}
                />
              </View>
            ) : null}
            {!isCourseLevelGroup(group) && group.removalRecommended && !isModulePendingDelete ? (
              <View style={styles.removalPill}>
                <TrashIcon size={9} color="#B42318" weight="bold" />
                <CustomText
                  text="Removal suggested"
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.removalPillText}
                />
              </View>
            ) : null}
            {!isCourseLevelGroup(group) && group.addRecommended ? (
              <View style={styles.addPill}>
                <PlusIcon size={9} color="#027A48" weight="bold" />
                <CustomText
                  text="Addition suggested"
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.addPillText}
                />
              </View>
            ) : null}
            {!isCourseLevelGroup(group) && group.estimatedMinutes != null ? (
              <CustomText
                text={formatModuleMinutes(group.estimatedMinutes)}
                useTranslationText={false}
                size="xs"
                weight="normal"
                style={styles.moduleTimeText}
              />
            ) : null}
            <CustomText
              text={`${decidedInGroup}/${group.total}`}
              useTranslationText={false}
              size="xs"
              weight="normal"
              style={decidedInGroup === group.total ? styles.sectionCountDone : styles.sectionCount}
            />
          </Pressable>
          {isDraggable ? (
            <Pressable
              onPointerDown={handleModuleDragStart}
              hitSlop={4}
              role="button"
              accessibilityRole="button"
              accessibilityLabel={`Drag ${group.title} module`}
              aria-label={`Drag ${group.title} module`}
              style={({ hovered, pressed }: PressableStateCallbackType) => [
                styles.moduleDragHandle,
                isActive && styles.moduleDragHandleDragging,
                (hovered || pressed) && styles.moduleDragHandleHover,
              ]}>
              <ModuleDragHandle />
            </Pressable>
          ) : null}
          <PopoverMenu
            triggerAccessibilityLabel={`${group.title} section options`}
            triggerStyle={styles.sectionDots}
            menuWidth={128}
            items={[
              ...(group.id !== "course-level-content" && !isModulePendingDelete && onAddActivity
                ? [
                    {
                      key: "add-activity",
                      label: "Add activity",
                      onPress: () => {
                        if (!permissions.canEdit) return;
                        onAddActivity(group);
                      },
                      disabled: !permissions.canEdit,
                    },
                  ]
                : []),
              ...(group.id !== "course-level-content" && !isModulePendingDelete && onDeleteModule
                ? [
                    {
                      key: "delete-module",
                      label: "Delete module",
                      danger: true,
                      onPress: () => {
                        if (!permissions.canEdit) return;
                        onDeleteModule(group);
                      },
                      disabled: !permissions.canEdit,
                    },
                  ]
                : []),
              ...(group.id !== "course-level-content" && isModulePendingDelete && onUndoDeleteModule
                ? [
                    {
                      key: "undo-delete-module",
                      label: "Undo delete",
                      onPress: () => {
                        if (!permissions.canRestoreVersion) return;
                        onUndoDeleteModule(group);
                      },
                      disabled: !permissions.canRestoreVersion,
                    },
                  ]
                : []),
              {
                key: "accept-all",
                label: "Accept all",
                onPress: () => {
                  if (!permissions.canQueue) return;
                  onAcceptGroup?.(group);
                },
                disabled: !permissions.canQueue,
              },
              {
                key: "skip-all",
                label: "Skip all",
                danger: true,
                onPress: () => {
                  if (!permissions.canQueue) return;
                  onRejectGroup?.(group);
                },
                disabled: !permissions.canQueue,
              },
            ]}
            renderTrigger={() => <DotsThreeIcon size={16} color={colors.grey[500]} weight="bold" />}
          />
        </View>

        {!isCollapsed ? (
          <>
            {group.items.filter(isModuleDescriptionItem).map((item) => renderSidebarItem(item))}

            {(() => {
              const orderedActivityItems = getOrderedActivityItemsForGroup(group);
              const draggableActivityItems = orderedActivityItems.filter((item) => item.pendingDelete !== true);
              const canDragGroupActivities =
                canReorderActivities &&
                !isCourseLevelGroup(group) &&
                !isModulePendingDelete &&
                ((Boolean(onActivityDragEnd) && draggableActivityItems.length > 1) ||
                  (canMoveActivitiesAcrossModules && draggableActivityItems.length > 0));
              const isActivityDropTarget =
                !isModulePendingDelete &&
                Boolean(crossModuleActivityDrag) &&
                crossModuleActivityDrag?.sourceGroupId !== group.id &&
                crossModuleActivityDropTarget?.targetGroupId === group.id;
              const showEmptyActivityDropZone =
                canMoveActivitiesAcrossModules &&
                !isCourseLevelGroup(group) &&
                !isModulePendingDelete &&
                orderedActivityItems.length === 0;

              if (!orderedActivityItems.length && !showEmptyActivityDropZone) {
                return null;
              }

              return (
                <View
                  testID={`module-activity-drop-zone-${group.id}`}
                  accessibilityLabel={`${group.title} activity drop zone`}
                  onPointerEnter={() => {
                    if (!isModulePendingDelete) {
                      updateCrossModuleActivityDropTarget(group.id);
                    }
                  }}
                  onPointerUp={handleCrossModuleActivityDrop}
                  style={[
                    styles.moduleActivityDropZone,
                    showEmptyActivityDropZone && styles.emptyActivityDropZone,
                    isActivityDropTarget && styles.moduleActivityDropZoneActive,
                  ]}>
                  {canDragGroupActivities ? (
                    <NestableDraggableFlatList
                      testID={`module-activity-draggable-list-${group.id}`}
                      data={orderedActivityItems}
                      onDragEnd={({ data }) => handleActivityDragEnd(group, data)}
                      keyExtractor={(item) => item.id}
                      renderItem={({ item, drag, isActive }) => renderSidebarItem(item, drag, isActive, group)}
                      dragItemOverflow
                      activationDistance={5}
                      autoscrollSpeed={50}
                      containerStyle={styles.moduleActivityList}
                    />
                  ) : (
                    orderedActivityItems.map((item) => renderSidebarItem(item, undefined, false, group))
                  )}
                </View>
              );
            })()}

            {group.id !== "course-level-content" && !isModulePendingDelete && onAddActivity ? (
              <Pressable
                onPress={() => {
                  if (!permissions.canEdit) return;
                  onAddActivity(group);
                }}
                disabled={!permissions.canEdit}
                role="button"
                accessibilityRole="button"
                accessibilityState={{ disabled: !permissions.canEdit }}
                accessibilityLabel={`Add activity to ${group.title}`}
                style={({ hovered }: PressableStateCallbackType) => [
                  styles.addActivityButton,
                  hovered && permissions.canEdit && styles.addActivityButtonHover,
                  !permissions.canEdit && styles.disabledAction,
                ]}>
                <PlusIcon
                  size={14}
                  color={permissions.canEdit ? colors.grey[600] : colors.grey[400]}
                  weight="regular"
                />
                <CustomText
                  text="Add activity"
                  useTranslationText={false}
                  size="xs"
                  weight="normal"
                  style={[styles.addActivityText, !permissions.canEdit && styles.disabledActionText]}
                />
              </Pressable>
            ) : null}
          </>
        ) : null}
      </View>
    );

    if (!isDraggable) {
      return section;
    }

    return <ScaleDecorator key={group.id}>{section}</ScaleDecorator>;
  };

  return (
    <View style={styles.sidebar}>
      <Pressable
        onPress={() => onSelectItem(null)}
        role="button"
        accessibilityRole="button"
        accessibilityLabel="Select overview"
        style={({ hovered }: PressableStateCallbackType) => [
          styles.overviewButton,
          selectedItemId === null && styles.overviewButtonActive,
          hovered && selectedItemId !== null && styles.sidebarItemHover,
        ]}>
        <CustomText text="Overview" useTranslationText={false} size="s" weight="bold" style={styles.overviewLabel} />
        <View style={styles.overviewScoreWrap}>
          <CustomText
            text={`${review.score}`}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.overviewScore}
          />
          <CustomText
            text={`/${review.maxScore}`}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.overviewScoreSuffix}
          />
        </View>
      </Pressable>

      <View style={styles.sidebarHeader}>
        <CustomText text="Modules" useTranslationText={false} size="s" weight="bold" style={styles.sidebarHeaderText} />
        <View style={styles.filterPills}>
          <FilterPill label="All" isActive={filterMode === "all"} onPress={() => onFilterModeChange("all")} />
          <FilterPill
            label="Undecided"
            isActive={filterMode === "undecided"}
            onPress={() => onFilterModeChange("undecided")}
          />
        </View>
      </View>

      {onAddModule ? (
        <Pressable
          onPress={() => {
            if (!permissions.canEdit) return;
            onAddModule();
          }}
          disabled={!permissions.canEdit}
          role="button"
          accessibilityRole="button"
          accessibilityState={{ disabled: !permissions.canEdit }}
          accessibilityLabel="Add module"
          style={({ hovered }: PressableStateCallbackType) => [
            styles.addModuleButton,
            hovered && permissions.canEdit && styles.addModuleButtonHover,
            !permissions.canEdit && styles.disabledAction,
          ]}>
          <PlusIcon size={14} color={permissions.canEdit ? colors.grey[600] : colors.grey[400]} weight="bold" />
          <CustomText
            text="Add module"
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={[styles.addModuleText, !permissions.canEdit && styles.disabledActionText]}
          />
        </Pressable>
      ) : null}

      {canUseNestableDrag ? (
        <NestableScrollContainer
          style={styles.sidebarScroll}
          contentContainerStyle={styles.sidebarScrollContent}
          tabIndex={0}
          testID="review-sidebar-scroll"
          onPointerUp={handleCrossModuleActivityDrop}
          showsVerticalScrollIndicator>
          {courseLevelGroups.map((group) => renderSidebarSection(group))}
          {canReorderModules ? (
            <NestableDraggableFlatList
              data={orderedModuleGroups}
              onDragEnd={handleModuleDragEnd}
              keyExtractor={(item) => item.id}
              renderItem={({ item, drag, isActive }) => renderSidebarSection(item, drag, isActive)}
              dragItemOverflow
              activationDistance={5}
              autoscrollSpeed={50}
              containerStyle={styles.draggableModuleList}
            />
          ) : (
            orderedModuleGroups.map((group) => renderSidebarSection(group))
          )}
        </NestableScrollContainer>
      ) : (
        <ScrollView
          style={styles.sidebarScroll}
          contentContainerStyle={styles.sidebarScrollContent}
          tabIndex={0}
          testID="review-sidebar-scroll"
          onPointerUp={handleCrossModuleActivityDrop}
          showsVerticalScrollIndicator>
          {review.sidebarGroups.map((group) => renderSidebarSection(group))}
        </ScrollView>
      )}

      <View style={styles.sidebarFooter}>
        <CustomText
          text={`${review.queuedCount} queued`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.queuedText}
        />
        <CustomText
          text={`${review.skippedCount} skipped`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.skippedText}
        />
        {review.publishedCount > 0 && (
          <CustomText
            text={`${review.publishedCount} published`}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.publishedText}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    width: 350,
    backgroundColor: "#FFFFFF",
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    flexDirection: "column",
    minHeight: 0,
  },
  overviewButton: {
    minHeight: 44,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  overviewButtonActive: {
    backgroundColor: "#F5F0FE",
  },
  overviewLabel: {
    color: colors.grey[900],
  },
  overviewScoreWrap: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  overviewScore: {
    color: colors.grey[900],
  },
  overviewScoreSuffix: {
    color: colors.grey[500],
  },
  sidebarHeader: {
    minHeight: 38,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarHeaderText: {
    color: colors.grey[700],
  },
  filterPills: {
    flexDirection: "row",
    gap: 4,
  },
  addModuleButton: {
    minHeight: 32,
    marginHorizontal: 8,
    marginVertical: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: colors.grey[50],
  },
  addModuleButtonHover: {
    backgroundColor: colors.grey[100],
  },
  addModuleText: {
    color: colors.grey[600],
  },
  disabledAction: {
    opacity: 0.6,
  },
  disabledActionText: {
    color: colors.grey[400],
  },
  sidebarScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  draggableModuleList: {
    minHeight: 0,
  },
  moduleActivityList: {
    minHeight: 0,
  },
  moduleActivityDropZone: {
    marginHorizontal: 4,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: "transparent",
    borderRadius: 8,
  },
  moduleActivityDropZoneActive: {
    borderColor: colors.brand[400],
    borderStyle: "dashed",
    backgroundColor: "#F5F8FF",
  },
  emptyActivityDropZone: {
    minHeight: 12,
    marginHorizontal: 8,
    marginVertical: 4,
  },
  sidebarScrollContent: {
    paddingBottom: 16,
    flexGrow: 1,
  },
  sidebarSection: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  sidebarSectionDragging: {
    backgroundColor: "#FFFFFF",
    opacity: 0.96,
    shadowColor: "#101828",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.14,
    shadowRadius: 10,
    elevation: 2,
  },
  sidebarSectionHeader: {
    minHeight: 39,
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  moduleDragHandle: {
    width: 30,
    height: 28,
    marginLeft: 8,
    marginRight: 4,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[50],
    ...(Platform.OS === "web" && ({ cursor: "grab" } as unknown as ViewStyle)),
  },
  moduleDragHandleHover: {
    borderColor: colors.grey[400],
    backgroundColor: colors.grey[100],
  },
  moduleDragHandleDragging: {
    borderColor: colors.brand[400],
    backgroundColor: "#EEF2FF",
    ...(Platform.OS === "web" && ({ cursor: "grabbing" } as unknown as ViewStyle)),
  },
  activityDragHandle: {
    width: 24,
    height: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[50],
    ...(Platform.OS === "web" && ({ cursor: "grab" } as unknown as ViewStyle)),
  },
  dragDotGrid: {
    gap: 3,
  },
  dragDotRow: {
    flexDirection: "row",
    gap: 3,
  },
  dragDot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: colors.grey[600],
  },
  sidebarSectionToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  sectionTitle: {
    color: colors.grey[700],
  },
  sectionCount: {
    color: colors.grey[500],
  },
  sectionCountDone: {
    color: colors.success[600],
  },
  pendingDeletePill: {
    backgroundColor: colors.error[50],
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  pendingDeletePillText: {
    color: colors.error[600],
    fontSize: 9,
    lineHeight: 12,
  },
  removalPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    backgroundColor: "#FEE4E2",
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  removalPillText: {
    color: "#B42318",
    fontSize: 9,
    lineHeight: 12,
  },
  addPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    backgroundColor: "#D1FADF",
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  addPillText: {
    color: "#027A48",
    fontSize: 9,
    lineHeight: 12,
  },
  moduleTimeText: {
    color: colors.grey[400],
    fontSize: 10,
    lineHeight: 14,
  },
  sectionDots: {
    marginLeft: 12,
    padding: 4,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  itemDots: {
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarItemActionSlot: {
    width: 26,
    minHeight: 24,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sidebarItemMenuSlot: {
    width: 24,
    minHeight: 24,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sidebarItem: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginHorizontal: 8,
    borderRadius: 8,
  },
  sidebarItemActive: {
    backgroundColor: "#F5F0FE",
  },
  sidebarItemDragging: {
    backgroundColor: "#FFFFFF",
    opacity: 0.96,
    shadowColor: "#101828",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 1,
  },
  sidebarItemDropTarget: {
    borderTopWidth: 2,
    borderTopColor: colors.brand[500],
    backgroundColor: "#F5F8FF",
  },
  sidebarItemHover: {
    backgroundColor: colors.grey[50],
  },
  addActivityButtonHover: {
    backgroundColor: colors.grey[50],
  },
  addActivityText: {
    color: colors.grey[600],
  },
  sidebarItemRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  sidebarItemContent: {
    flex: 1,
    minWidth: 0,
  },
  sidebarRadio: {
    width: 14,
    height: 14,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: colors.grey[300],
    backgroundColor: "#FFFFFF",
  },
  decisionIconBackground: {
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarItemTitle: {
    color: colors.grey[700],
    lineHeight: 18,
  },
  commentMarkerWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  commentMarkerIcon: {
    flexShrink: 0,
  },
  commentMarkerCount: {
    color: "#a855f7",
    fontSize: 11,
    lineHeight: 14,
  },
  commentMarkerResolved: {
    opacity: 0.6,
  },
  commentMarkerCountResolved: {
    color: "#6B7280",
  },
  publishedPill: {
    paddingHorizontal: 10,
    height: 21,
    justifyContent: "center",
    borderRadius: 9999,
    backgroundColor: "#CCD7FF",
  },
  publishedPillText: {
    color: "#000B33",
    fontSize: 10,
  },
  sidebarItemMeta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: 0,
    marginTop: 2,
    flexWrap: "wrap",
  },
  addActivityButton: {
    minHeight: 32,
    marginHorizontal: 8,
    marginTop: 4,
    marginBottom: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: colors.white,
  },
  sidebarPill: {
    borderRadius: 8,
    paddingHorizontal: 8,
    justifyContent: "center",
    height: 24,
  },
  manualPill: {
    borderRadius: 8,
    paddingHorizontal: 8,
    justifyContent: "center",
    height: 24,
    backgroundColor: "#F5F0FE",
  },
  sidebarFooterTextRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  sidebarFooterText: {
    fontSize: 11,
    lineHeight: 16,
  },
  manualMetaText: {
    color: "#7244BB",
    fontSize: 10,
    letterSpacing: 0.4,
  },
  manualItemTypeText: {
    color: "#667085",
    fontSize: 11,
    lineHeight: 16,
  },
  sidebarFooter: {
    minHeight: 34,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  queuedText: {
    color: "#3F7F18",
    fontSize: 11,
    lineHeight: 16,
  },
  skippedText: {
    color: "#D92D20",
    fontSize: 11,
    lineHeight: 16,
  },
  publishedText: {
    color: colors.brand[600],
    fontSize: 11,
    lineHeight: 16,
  },
});

export default ReviewSidebar;
